/* eslint-disable no-nested-ternary */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  Drawer,
  Grid,
  IconButton,
  Typography,
  Alert,
  Box,
  Portal,
  Snackbar,
  CircularProgress,
  Pagination,
  TextField,
  InputAdornment,
  Stack,
} from '@mui/material';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { consoleCommands } from 'modules/common/ui-controller';
import { decodeLegacyCommandUrl, isHistoricalViResult } from 'modules/common/ui-controller/utils/functions';
import { VI_RESPONSE_DELAY } from 'modules/common/ui-controller/utils/shared-command-steps';
import { selectAuthUser, selectOrganizationId } from 'modules/common/auth/selectors';
import {
  selectCurrentUrl,
  selectEncodedTabList,
  selectHotelDropdownList,
  selectIsDemoModeActivated,
  selectIsDemoUser,
  selectLLMErrorResponse,
  selectLLMImageResponse,
  selectOpenVisualAgentModal,
  selectViResponseListError,
  selectViResponseListLoading,
  selectVisualAnalysis,
  selectTabValue
} from 'modules/dashboard/selectors';
import { dashboardActions } from 'modules/dashboard/slice';
import { usersActions } from 'modules/users/slice';
import { selectInterpreterAgentError } from 'modules/users/selectors';
import ERROR_TYPES from 'modules/common/constants/error-types';
import { ENVIRONMENT } from 'config';
import PAGINATION from 'modules/common/constants/pagination-limits';
import ORDER_BY from 'modules/common/constants/order-by';
import { X } from 'react-feather';
import { Clear, Search } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import isFeatureEnabled from 'modules/common/utils/string';
import createHotelNameAnonymizer, {
  createHotelNameDeanonymizer,
} from 'modules/common/utils/anonymize-hotel-names';
// import GridFeedback from './feedback';
import WarningMessage from './warning-message';
import {
  drawerPaperPropsStyles,
  historicalBackdropStyles,
} from './style';
import ResponseItem from './response-item';
import SearchFilterPopover from './search-filter';

/**
 * Modal LLM common component that uses across modules for display warning and confirmation messages
 * @param {*} props
 * @returns
 */
const InterpreterAgentResponseModal = (props) => {
  //
  const dispatch = useDispatch();
  //
  const organizationId = useSelector(selectOrganizationId);
  const authUser = useSelector(selectAuthUser);
  const imageResponse = useSelector(selectLLMImageResponse);
  const errorResponse = useSelector(selectLLMErrorResponse);
  const visualAnalysis = useSelector(selectVisualAnalysis);
  const viResponseListError = useSelector(selectViResponseListError);
  const InterpreterAgentError = useSelector(selectInterpreterAgentError);
  const openVisualAgentModal = useSelector(selectOpenVisualAgentModal);
  const loading = useSelector(selectViResponseListLoading);
  const currentShareUrl = useSelector(selectCurrentUrl);
  const encodedTabs = useSelector(selectEncodedTabList);
  const tabValue = useSelector(selectTabValue);
  const hash = encodedTabs?.[tabValue];
  const hotelList = useSelector(selectHotelDropdownList);
  const isDemoUser = useSelector(selectIsDemoUser);
  const isDemoModeActivated = useSelector(selectIsDemoModeActivated);
  // Replaces the actual hotel names mentioned in the responses with the demo mode hotel labels
  const anonymize = useMemo(
    () =>
      isDemoUser || isDemoModeActivated ? createHotelNameAnonymizer(hotelList, isDemoUser) : null,
    [hotelList, isDemoUser, isDemoModeActivated]
  );
  // Replaces the demo mode hotel labels in the titles of the responses created in demo mode with
  // the actual hotel names
  const deanonymizeTitle = useMemo(
    () => (!isDemoUser && !isDemoModeActivated ? createHotelNameDeanonymizer(hotelList) : null),
    [hotelList, isDemoUser, isDemoModeActivated]
  );

  const { handleClose, title } = props;
  const location = useLocation();
  // response of a historical record url that should be expanded and the page it is on
  const [historyTarget] = useState(() => {
    const params = new URLSearchParams(decodeLegacyCommandUrl(location?.search));
    return {
      id: params.get(consoleCommands.VI_HISTORY),
      page: parseInt(params.get(consoleCommands.VI_HISTORY_PAGE), 10) || 1,
    };
  });
  const [targetResponseId, setTargetResponseId] = useState(historyTarget.id);
  const [expanded, setExpanded] = useState(0);
  const [copied, setCopied] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  // Search & Pagination States
  const [page, setPage] = useState(historyTarget.page);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchMode, setSearchMode] = useState('semantic');
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState('');
  // Whether the user has any past interactions to search over (only unfiltered fetches update it)
  const [hasHistory, setHasHistory] = useState(false);
  const isSearching = submittedSearchTerm.trim().length > 0;

  useEffect(() => {
    if (!loading && !isSearching && !viResponseListError) {
      setHasHistory(visualAnalysis?.results?.length > 0);
    }
  }, [loading, isSearching, viResponseListError, visualAnalysis]);

  // index of the response of a historical record url in the list, -1 until it is loaded
  const targetIndex = targetResponseId
    ? visualAnalysis?.results?.findIndex((result) => String(result?.id) === targetResponseId) ?? -1
    : -1;
  const isTargetFound = targetIndex >= 0;

  // Expand the response of a historical record url once it is in the list
  useEffect(() => {
    if (targetIndex >= 0) setExpanded(targetIndex);
  }, [targetIndex]);

  // Open the drawer for a historical record url after the dashboard is given time to settle
  useEffect(() => {
    if (!isTargetFound) return undefined;
    const timer = setTimeout(() => {
      dispatch(dashboardActions.setOpenVisualAgentModal(true));
    }, VI_RESPONSE_DELAY);
    return () => clearTimeout(timer);
  }, [isTargetFound, dispatch]);

  // Expand or collapse a response, the response of a historical record url is no longer followed
  const handleExpand = (value) => {
    setTargetResponseId(null);
    setExpanded(value);
  };

  // 1. Helper function to fetch response list with search query
  const fetchResponses = useCallback(
    (pageNumber, mode, search = '') => {
      if (!authUser || !organizationId) return;

      // Base query params
      let query = `limit=${PAGINATION.VI_RESPONSES_PER_PAGE}&page=${pageNumber}&sortBy=createdAt:${ORDER_BY.DESCENDING}`;

      // Append search term if provided (mode 'semantic' = BM25 + semantic hybrid search, 'full-text' = exact match)
      if (search.trim()) {
        query += `&search=${encodeURIComponent(search.trim())}&mode=${encodeURIComponent(mode)}`;
      }

      dispatch(
        dashboardActions.getUserViResponseList({
          organizationId,
          userId: authUser?.id,
          query,
        })
      );
    },
    [authUser, organizationId, dispatch]
  );

  // 2. Trigger API fetch whenever page, submitted search term or mode changes
  useEffect(() => {
    if (authUser && organizationId) {
      fetchResponses(page, searchMode, submittedSearchTerm);
    }
  }, [organizationId, authUser, page, submittedSearchTerm, searchMode, fetchResponses]);

  // 3. Whether the response list request was aborted by closing the drawer, refetched on reopen
  const abortedFetchRef = useRef(false);
  useEffect(() => {
    if (openVisualAgentModal && abortedFetchRef.current) {
      abortedFetchRef.current = false;
      fetchResponses(page, searchMode, submittedSearchTerm);
    }
  }, [openVisualAgentModal]);

  // 4. Close the opened VI result when it does not belong to the current share url (tab or filter change)
  // and open the first result generated from the current share url, if any.
  // The response of a historical record url is the exception, it stays expanded while it is followed
  const firstResultId = visualAnalysis?.results?.[0]?.id;
  useEffect(() => {
    const results = visualAnalysis?.results ?? [];
    const isHistorical = (index) =>
      isHistoricalViResult(results[index]?.response?.url, currentShareUrl, hash);
    setExpanded((prev) => {
      // targetIndex is not a dependency: un-following the response should not close the result
      // the user has just opened
      if (targetIndex >= 0) return targetIndex;
      if (prev !== null && results[prev] && !isHistorical(prev)) return prev;
      // search results start collapsed
      if (isSearching) return null;
      const currentIndex = results.findIndex(
        (result) => result?.response?.url === `${currentShareUrl}#${hash}`
      );
      return currentIndex === -1 ? null : currentIndex;
    });
  }, [currentShareUrl, hash, openVisualAgentModal, firstResultId, isSearching]);

  // Whether the opened VI result was generated from a share url other than the current one
  const isHistoricalViResultOpen =
    expanded !== null &&
    isHistoricalViResult(visualAnalysis?.results?.[expanded]?.response?.url, currentShareUrl, hash);

  // Publish the greyed out overlay state, the navbar blurs the selected hotel and room count with it
  const isOverlayActive = openVisualAgentModal && isHistoricalViResultOpen;
  useEffect(() => {
    dispatch(dashboardActions.setIsHistoricalViOverlayActive(isOverlayActive));
  }, [isOverlayActive, dispatch]);
  // the drawer is only mounted in the dashboard, reset when leaving it
  useEffect(
    () => () => dispatch(dashboardActions.setIsHistoricalViOverlayActive(false)),
    [dispatch]
  );

  // Bottom of the navbar, the greyed out overlay starts below it so that the navbar stays active
  const [navbarBottom, setNavbarBottom] = useState(null);
  useLayoutEffect(() => {
    if (!openVisualAgentModal || !isHistoricalViResultOpen) {
      return () => {};
    }
    const measure = () => {
      setNavbarBottom(
        document.querySelector('.app-navbar')?.getBoundingClientRect().bottom ?? null
      );
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [openVisualAgentModal, isHistoricalViResultOpen]);

  // Handle Page Change
  const handlePageChange = (event, value) => {
    setTargetResponseId(null);
    setPage(value);
  };

  const handleExited = () => {
    // abort the pending response list request so that it does not keep running after closing
    if (loading) {
      dispatch(dashboardActions.cancelUserViResponseList());
      // a dropped search is replaced by the unfiltered list below, other requests run again on reopen
      if (!isSearching) abortedFetchRef.current = true;
    }
    // the search is not kept between openings of the drawer, the unfiltered list is fetched again
    setSearchTerm('');
    if (isSearching) {
      setSubmittedSearchTerm('');
      setPage(1);
    }
    if (imageResponse?.length > 0) {
      dispatch(
        dashboardActions.updateNotification({
          organizationId,
          userId: imageResponse?.[0]?.userId,
          notificationId: imageResponse?.[0]?.id,
          isOpen: true,
        })
      );
    }
    if (errorResponse?.length > 0) {
      dispatch(
        dashboardActions.updateNotification({
          organizationId,
          userId: errorResponse?.[0]?.userId,
          notificationId: errorResponse?.[0]?.id,
          isOpen: true,
        })
      );
    }
    dispatch(dashboardActions.resetLLmScanData());
    setTargetResponseId(null);
    setExpanded(0);
    dispatch(usersActions.setInterpreterAgentMessage(''));
  };
  //
  const handleToggle = () => {
    setTargetResponseId(null);
    dispatch(dashboardActions.setOpenVisualAgentModal(!openVisualAgentModal));
    dispatch(usersActions.setInterpreterAgentMessage(''));
  };
  //
  // Handle Search Input Change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Run the search only when Enter is pressed
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setTargetResponseId(null);
      setExpanded(null); // New search results start collapsed
      setSubmittedSearchTerm(searchTerm);
      setPage(1); // Reset to page 1 on new search query
    }
  };

  // Clear Search Input and restore the unfiltered list
  const handleClearSearch = () => {
    setTargetResponseId(null);
    setExpanded(null);
    setSearchTerm('');
    setSubmittedSearchTerm('');
    setPage(1);
  };

  // Changing the mode re-runs the search, its results start collapsed
  const handleSearchModeChange = (mode) => {
    if (isSearching) setExpanded(null);
    setSearchMode(mode);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Drawer
        anchor="right"
        open={openVisualAgentModal}
        onClose={(event, reason) => {
          handleClose(event, reason);
          handleExited();
        }}
        variant="temporary"
        // the root should not block the clicks on the navbar that stays active above the overlay
        sx={isHistoricalViResultOpen ? { pointerEvents: 'none' } : undefined}
        slotProps={{
          backdrop: {
            style: isHistoricalViResultOpen
              ? {
                  ...historicalBackdropStyles,
                  top: navbarBottom ?? 0,
                }
              : { backgroundColor: 'transparent' },
          },
        }}
        ModalProps={{
          disableEnforceFocus: isHistoricalViResultOpen,
          // the drawer is closed by the backdrop click or the close button only
          disableEscapeKeyDown: true,
          BackdropProps: {
            sx: {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          },
        }}
        PaperProps={{
          sx: drawerPaperPropsStyles,
        }}
      >
        <Grid container direction="row" justifyContent="space-between">
          <Portal>
            <Snackbar
              autoHideDuration={3000}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={copied}
              onClose={() => {
                setCopied(false);
              }}
            >
              <Alert severity="success">Report copied to clipboard!</Alert>
            </Snackbar>
            <Snackbar
              autoHideDuration={3000}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={copiedUrl}
              onClose={() => {
                setCopiedUrl(false);
              }}
            >
              <Alert severity="success">Share URL copied to clipboard!</Alert>
            </Snackbar>
          </Portal>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title ?? 'Visual Interpreter Agent'}
          </Typography>
          <Grid>
            <IconButton
              color="inherit"
              size="small"
              onClick={() => {
                handleExited();
                handleToggle();
              }}
            >
              <X size={17} />
            </IconButton>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          direction="column"
          flexWrap="nowrap"
          sx={{
            height: '85vh',
          }}
        >
          {InterpreterAgentError && (
            <Alert
              severity={ERROR_TYPES.ERROR}
              sx={{
                mt: 2,
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                width: '100%',
              }}
            >
              {InterpreterAgentError}
            </Alert>
          )}
          <Grid
            item
            sx={{
              color: '#1f1f1f',
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              marginTop: 2,
              px: 1.5,
            }}
          >
            {/* Search Bar Input */}
            {isFeatureEnabled(ENVIRONMENT.ENABLE_VI_SEARCH_FEATURE) && hasHistory && (
              <Stack sx={{ my: 2 }} direction="row" spacing={2}>
                <TextField
                  sx={{
                    borderRadius: 10,
                    '& input::placeholder': { color: '#667085', fontStyle: 'italic', opacity: 1 },
                    '& input:focus::placeholder': { opacity: 0 },
                  }}
                  fullWidth
                  size="small"
                  placeholder="Search over your past interactions"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearSearch}>
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <SearchFilterPopover searchMode={searchMode} setSearchMode={handleSearchModeChange} />
              </Stack>
            )}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : viResponseListError ? (
              <Alert severity={ERROR_TYPES.ERROR} sx={{ mt: 2 }}>
                Your action could not be completed at this time. Please try again later.
              </Alert>
            ) : isSearching && !visualAnalysis?.results?.length ? (
              <Typography variant="body1" sx={{ textAlign: 'center', color: '#667085', py: 4 }}>
                Your search returned no results.
              </Typography>
            ) : (
              visualAnalysis &&
              visualAnalysis?.results?.map((msg, index) => (
                <ResponseItem
                  key={msg.id}
                  msg={msg}
                  index={index}
                  expanded={expanded}
                  page={isSearching ? undefined : page}
                  anonymize={anonymize}
                  deanonymizeTitle={deanonymizeTitle}
                  setCopied={setCopied}
                  setCopiedUrl={setCopiedUrl}
                  setExpanded={handleExpand}
                />
              ))
            )}
          </Grid>
          {/* Numbered Pagination Control Panel */}
          {visualAnalysis.totalPages > 1 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={2}
              sx={{
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <Pagination
                count={visualAnalysis.totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
                disabled={loading}
              />
            </Box>
          )}
          <WarningMessage />
          {/* Commented for the release */}
          {/* <GridFeedback /> */}
        </Grid>
      </Drawer>
    </Box>
  );
};
//
export default InterpreterAgentResponseModal;

